import React from 'react';
import { motion } from 'framer-motion'; 
import ProjectStatsBadge from '../../../components/ProjectStatsBadge';
import ShareProjectButton from '../../../components/ShareProjectButton/ShareProjectButton';
import './easy-hub.css';
import aeroglassProImage from '../../../assets/images/aeroglass.png';

const ToDoListApp = () => {
  return (
    <motion.div 
      className="project-detail-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <motion.h1 
        className="project-heading"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        AeroGlass <span>Pro</span>
      </motion.h1>

      <motion.p 
        className="last-updated"
        initial={{ x: 50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      ><br />
        Last Updated On: 11th July 2026
      </motion.p>

      <div className="project-content">
        <motion.div 
          className="project-left"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <img src={aeroglassProImage} alt="To-Do List App" className="project-image" />
          <h2 className="project-subtitle">AeroGlass Pro</h2>
          <span className="project-category">Website</span>
          <p className="project-author">Author: Praveen Kumar B</p>

          <ProjectStatsBadge docId="aeroGlassPage" trackView={true} />

          {/* Buttons */}
          <div className="project-buttons">
            <a href="https://productlanding-project.netlify.app/" className="visit-project-button">
              → Visit the Project
            </a>
            <a href="https://github.com/Praveenkumar-king/Product-Landing-Page" className="visit-project-button">
              → View on GitHub
            </a>
            <ShareProjectButton
              project={{
                id: "aeroGlassPage",
                title: "AeroGlass Product Landing Page",
                path: "/projects/projectdetail/aeroglass-productlanding-page"
              }}
            />
          </div>
        </motion.div>

        <motion.div 
          className="project-right"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <h3 className="section-title">Project Details</h3>
          <p className="overview-text">
          Welcome to <strong>Aeroglass Pro</strong>! Explore a premium product landing page crafted to showcase an innovative smart wearable with a sleek user experience.
          </p>

          <h3 className="section-title">Overview 🌟</h3>
          <p className="overview-description">
            The <strong>Aeroglass Pro</strong> landing page presents a futuristic smart wearable through an immersive and visually engaging experience. Designed with modern UI principles, it highlights product features, specifications, pricing, testimonials, and compelling call-to-action sections to maximize user engagement.
          </p>

          <h3 className="section-title">Key Features of the App 🛠️</h3>
          <ul className="feature-list">
            <li><strong>Hero Banner</strong> 🚀 - Eye-catching product showcase with engaging visuals.</li>
            <li><strong>Feature Highlights</strong> ✨ - Interactive cards presenting premium capabilities.</li>
            <li><strong>Specifications</strong> ⚙️ - Detailed technical information for users.</li>
            <li><strong>Product Gallery</strong> 🖼️ - High-quality product images from multiple angles.</li>
            <li><strong>Pricing Section</strong> 💰 - Clear pricing plans with purchase CTA.</li>
            <li><strong>Testimonials</strong> ⭐ - Customer reviews and user feedback.</li>
            <li><strong>Responsive Design</strong> 📱 - Optimized for mobile, tablet, and desktop devices.</li>
          </ul>

          <h3 className="section-title">How the Website Works 🔍</h3>
          <ul className="how-it-works-list">
           <li><strong>Discover</strong> 🔍 - Explore the product through an engaging hero section.</li>
           <li><strong>Learn</strong> 📖 - View detailed features and technical specifications.</li>
           <li><strong>Compare</strong> ⚖️ - Understand product advantages and benefits.</li>
           <li><strong>Purchase</strong> 🛒 - Complete the journey using the call-to-action section.</li>
          </ul>

          <h3 className="section-title">Features and Benefits 🚀</h3>
          <ul className="benefits-list">
             <li><strong>Premium UI</strong> 🎨 - Elegant, modern landing page design.</li>
             <li><strong>Better User Engagement</strong> 💡 - Interactive sections improve visitor experience.</li>
             <li><strong>Fast Performance</strong> ⚡ - Lightweight and optimized interface.</li>
             <li><strong>Fully Responsive</strong> 📱 - Seamless experience across all devices.</li>
          </ul>

          <h3 className="section-title">Technical Details ⚙️</h3>
          <ul className="technical-details-list">
             <li><strong>Frontend</strong> 🖥️ - React.js, HTML5, CSS3, JavaScript (ES6).</li>
             <li><strong>Animations</strong> ✨ - Framer Motion for smooth page transitions.</li>
             <li><strong>Styling</strong> 🎨 - CSS Flexbox, Grid, and modern responsive layouts.</li>
             <li><strong>Deployment</strong> ☁️ - Hosted on Netlify.</li>
          </ul>

          <h3 className="section-title">Real-Life Applications 🌍</h3>
          <ul className="real-life-applications-list">
           <li><strong>Product Marketing</strong> 🚀 - Launch premium consumer products.</li>
           <li><strong>Startup Websites</strong> 💼 - Showcase innovative technology products.</li>
           <li><strong>Electronics Brands</strong> 📱 - Promote smart devices and gadgets.</li>
           <li><strong>E-commerce Campaigns</strong> 🛍️ - Increase product visibility and conversions.</li>
          </ul>

          <h3 className="section-title">Future Features 🔮</h3>
          <ul className="future-features-list">
            <li><strong>Product Comparison</strong> 📊 - Compare different product models.</li>
            <li><strong>360° Product View</strong> 🔄 - Interactive product visualization.</li>
            <li><strong>Customer Reviews</strong> ⭐ - Dynamic review and rating system.</li>
            <li><strong>Online Checkout</strong> 💳 - Complete e-commerce purchasing flow.</li>
          </ul>

          <h3 className="section-title">Conclusion 🎯</h3>
          <p>The <strong>Aeroglass Pro</strong> landing page demonstrates how modern product websites combine compelling visuals, responsive layouts, interactive feature sections, customer testimonials, and persuasive calls to action to create a premium user experience and improve conversion rates.</p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ToDoListApp;
